/**
 * ProductGridManager - Core functionality for product grids
 * Handles shared logic between collection and search grids
 */

class ProductGridManager {
  constructor(options = {}) {
    this.container = options.container;
    this.productsPerPage = options.productsPerPage || 12;
    this.products = [];
    this.currentPage = 1;
    this.filters = {
      includeSold: false,
      maker: '',
      era: '',
      sort: 'recent'
    };

    this.productList = document.getElementById('product-list');
    this.cacheStatus = document.getElementById('cache-status');
    this.debugPanel = document.getElementById('debug-panel');
    this.filterSummary = document.getElementById('filter-summary');

    // Filter elements
    this.includeSoldCheckbox = document.getElementById('include-sold-out');
    this.makerFilter = document.getElementById('maker-filter');
    this.eraFilter = document.getElementById('era-filter');
    this.sortFilter = document.getElementById('sort-filter');

    this.setupEventListeners();
    this.setupDebugMode();
  }

  setupEventListeners() {
    if (this.includeSoldCheckbox) {
      this.includeSoldCheckbox.addEventListener('change', () => {
        this.filters.includeSold = this.includeSoldCheckbox.checked;
        this.currentPage = 1;
        this.updateURL();
        this.renderGrid();

        // Sync header checkbox if it exists
        const headerCheckbox = document.getElementById('search__sold-items__checkbox');
        if (headerCheckbox) {
          headerCheckbox.checked = this.filters.includeSold;
        }
      });
    }

    if (this.makerFilter) {
      this.makerFilter.addEventListener('change', () => {
        this.filters.maker = this.makerFilter.value;
        this.currentPage = 1;
        this.updateURL();
        this.renderGrid();
      });
    }

    if (this.eraFilter) {
      this.eraFilter.addEventListener('change', () => {
        this.filters.era = this.eraFilter.value;
        this.currentPage = 1;
        this.updateURL();
        this.renderGrid();
      });
    }

    if (this.sortFilter) {
      this.sortFilter.addEventListener('change', () => {
        this.filters.sort = this.sortFilter.value;
        this.currentPage = 1;
        this.updateURL();
        this.renderGrid();
      });
    }

    // Handle browser back/forward buttons
    window.addEventListener('popstate', (event) => {
      if (event.state && event.state.gridFilters) {
        this.restoreStateFromURL(false);
        this.renderGrid();
      }
    });
  }

  setupDebugMode() {
    const isDebugMode = window.location.search.includes('debug=cache');
    if (isDebugMode && this.debugPanel) {
      this.debugPanel.style.display = 'block';
      this.updateDebugPanel();
      setInterval(() => this.updateDebugPanel(), 2000);
    }

    if (isDebugMode && this.cacheStatus) {
      this.cacheStatus.style.display = 'block';
      this.updateCacheStatus('Initializing...');
    }
  }

  getFilteredProducts() {
    // For collections: Products are pre-filtered by AjaxFilterManager, just return them
    // For search: Subclass can override this method
    return this.products;
  }

  sortProducts(products) {
    switch (this.filters.sort) {
      case 'recent':
        // Server returns products in CREATED desc order (= most recently modified).
        // No client-side re-sort needed.
        return products;

      case 'price_low':
        return products.sort((a, b) => {
          const priceA = parseFloat(a.price) || 0;
          const priceB = parseFloat(b.price) || 0;
          return priceA - priceB;
        });

      case 'price_high':
        return products.sort((a, b) => {
          const priceA = parseFloat(a.price) || 0;
          const priceB = parseFloat(b.price) || 0;
          return priceB - priceA;
        });

      case 'maker_az':
        return products.sort((a, b) => {
          const vendorA = a.vendor || '';
          const vendorB = b.vendor || '';
          return vendorA.localeCompare(vendorB);
        });

      default:
        // Default to server order (CREATED desc = most recently modified)
        return products;
    }
  }

  getCurrentPageProducts() {
    const filtered = this.getFilteredProducts();
    
    // Use stored total count for fast pagination when no filters are active
    // Otherwise use the actual filtered count
    const totalForPagination = this.totalProductCount && !this.hasActiveFilters()
      ? this.totalProductCount
      : filtered.length;
    
    const totalPages = Math.ceil(totalForPagination / this.productsPerPage);
    
    // If current page is out of bounds, reset to page 1
    if (this.currentPage > totalPages && totalPages > 0) {
      console.log(`Page ${this.currentPage} out of bounds (max: ${totalPages}), resetting to page 1`);
      this.currentPage = 1;
      this.updateURL();
    }
    
    const startIndex = (this.currentPage - 1) * this.productsPerPage;
    const endIndex = startIndex + this.productsPerPage;
    return {
      products: filtered.slice(startIndex, endIndex),
      totalFiltered: totalForPagination,
      totalPages: totalPages
    };
  }

  hasActiveFilters() {
    return this.filters.maker || this.filters.era || this.filters.includeSold;
  }

  renderGrid() {
    const pageData = this.getCurrentPageProducts();

    if (!this.productList) return;

    if (!pageData.products || pageData.products.length === 0) {
      this.productList.innerHTML = '<li class="no-products">Could not find any instruments to match those filters.</li>';
      this.hidePagination();
      return;
    }

    this.renderProductList(pageData.products);
    
    // Skip pagination rendering if we're waiting for the count to load
    if (!this.awaitingPaginationCount) {
      this.renderPagination(pageData.totalFiltered, pageData.totalPages);
    }
  }

  renderProductList(products) {
    const template = document.getElementById('product-item-template');
    const fragment = document.createDocumentFragment();

    products.forEach((product) => {
      const clone = template.content.cloneNode(true);
      const listItem = clone.querySelector('.product-item');

      listItem.setAttribute('data-product-id', product.id);
      clone.querySelector('.sku-value').textContent = product.sku || 'No SKU';

      if (product.available && product.price) {
        clone.querySelector('.product-price').textContent = `$${parseFloat(product.price).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}`;
      } else {
        clone.querySelector('.product-price').textContent = '';
      }

      const justArrivedElement = clone.querySelector('.product-just-arrived');
      if (product.justArrived === true || product.justArrived === 'true') {
        justArrivedElement.style.display = 'block';
      }

      listItem.classList.add(product.available ? 'available' : 'sold-out');
      fragment.appendChild(clone);
    });

    // Hide loading message and show products
    const loadingMessage = document.getElementById('loading-message');
    if (loadingMessage) loadingMessage.style.display = 'none';

    this.productList.innerHTML = '';
    this.productList.appendChild(fragment);

    // Fetch display data asynchronously
    this.fetchDisplayData(products);
  }

  async fetchDisplayData(products) {
    // This will be implemented by the specific grid controllers
    // (SearchGridController, CollectionGridController)
    console.log('fetchDisplayData should be implemented by subclass');
  }

  updateURL(pushState = true) {
    const urlParams = new URLSearchParams();

    if (this.filters.includeSold) {
      urlParams.set('include_sold', 'true');
    }
    if (this.filters.maker) {
      urlParams.set('maker', this.filters.maker);
    }
    if (this.filters.era) {
      urlParams.set('era', this.filters.era);
    }
    if (this.filters.sort && this.filters.sort !== 'recent') {
      urlParams.set('sort', this.filters.sort);
    }
    if (this.currentPage > 1) {
      urlParams.set('page', this.currentPage.toString());
    }

    // Preserve debug parameters
    const currentParams = new URLSearchParams(window.location.search);
    if (currentParams.get('debug')) {
      urlParams.set('debug', currentParams.get('debug'));
    }

    const newUrl = window.location.pathname + (urlParams.toString() ? '?' + urlParams.toString() : '');

    if (pushState) {
      const state = {
        gridFilters: {
          includeSold: this.filters.includeSold,
          maker: this.filters.maker,
          era: this.filters.era,
          sort: this.filters.sort,
          page: this.currentPage
        }
      };
      window.history.pushState(state, '', newUrl);
    }
  }

  restoreStateFromURL(updateURL = true) {
    const urlParams = new URLSearchParams(window.location.search);

    this.filters.includeSold = urlParams.get('include_sold') === 'true';
    this.filters.maker = urlParams.get('maker') || '';
    this.filters.era = urlParams.get('era') || '';
    this.filters.sort = urlParams.get('sort') || 'recent';
    this.currentPage = parseInt(urlParams.get('page')) || 1;

    // Update form controls
    if (this.includeSoldCheckbox) {
      this.includeSoldCheckbox.checked = this.filters.includeSold;
    }
    if (this.makerFilter) {
      this.makerFilter.value = this.filters.maker;
    }
    if (this.eraFilter) {
      this.eraFilter.value = this.filters.era;
    }
    if (this.sortFilter) {
      this.sortFilter.value = this.filters.sort;
    }

    if (updateURL) {
      this.updateURL(false);
    }
  }

  renderPaginationSimple() {
    if (!this.paginationManager) {
      this.paginationManager = new PaginationManager(this);
      window.activePaginationManager = this.paginationManager;
    }
    this.paginationManager.render(0, 999, { simpleMode: true });
  }

  showPaginationLoading() {
    if (this.paginationManager) {
      this.paginationManager.showLoading();
    } else {
      const topLoading = document.getElementById('pagination-top-loading');
      const bottomLoading = document.getElementById('pagination-bottom-loading');
      const topContent = document.getElementById('pagination-top-content');
      const bottomContent = document.getElementById('pagination-bottom-content');
      if (topLoading) topLoading.style.display = 'flex';
      if (bottomLoading) bottomLoading.style.display = 'flex';
      if (topContent) topContent.style.display = 'none';
      if (bottomContent) bottomContent.style.display = 'none';
    }
  }

  renderPagination(totalFiltered, totalPages) {
    if (!this.paginationManager) {
      this.paginationManager = new PaginationManager(this);
      window.activePaginationManager = this.paginationManager; // For global access
    }
    this.paginationManager.render(totalFiltered, totalPages);
  }

  hidePagination() {
    const paginationTop = document.getElementById('pagination-top-content');
    const paginationBottom = document.getElementById('pagination-bottom-content');
    if (paginationTop) paginationTop.style.display = 'none';
    if (paginationBottom) paginationBottom.style.display = 'none';
  }

  updateCacheStatus(message, success = null, isError = false) {
    if (!this.cacheStatus) return;

    const indicatorText = document.getElementById('cache-indicator-text');
    if (!indicatorText) return;

    indicatorText.textContent = message;
    this.cacheStatus.classList.remove('loading', 'success', 'error');

    if (isError) {
      this.cacheStatus.classList.add('error');
    } else if (success === true) {
      this.cacheStatus.classList.add('success');
    } else {
      this.cacheStatus.classList.add('loading');
    }
  }

  updateDebugPanel() {
    const debugContent = document.getElementById('debug-content');
    if (!debugContent) return;

    debugContent.innerHTML = `
      <div class="debug-item"><strong>Products Loaded:</strong> ${this.products.length}</div>
      <div class="debug-item"><strong>Current Page:</strong> ${this.currentPage}</div>
      <div class="debug-item"><strong>Filters:</strong> ${JSON.stringify(this.filters)}</div>
    `;
  }
}

// Export for use by other modules
window.ProductGridManager = ProductGridManager;
